import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { assistanceTeamServices } from '../../services/assistanceTeamServices';
import { teamServices } from '../../services/teamServices';
import { useAuth } from '../../services/authentication/authContext';
import {
  Grid2,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box
} from '@mui/material';
import TableSkeleton from '../common/TableSkeleton';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PageHeader from '../ui/PageHeader';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';
import { useSnackbar } from '../../utils/useSnackbar';

const ASSISTANCE_TEAM_SORT_FIELD_MAP = {
  name: 'name',
  parentTeamName: 'parentTeam.name',
  ownerFirstName: 'ownerId.firstName',
  ownerLastName: 'ownerId.lastName',
  ownerEmail: 'ownerId.emailId',
  id: 'id',
  updatedAt: 'updatedAt',
  createdAt: 'createdAt'
};

const AssistanceTeams = () => {
  const [assistanceTeamData, setAssistanceTeamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { accessToken } = useAuth();
  const [searchTeam, setSearchTeam] = useState('');
  const [debouncedSearchTeam, setDebouncedSearchTeam] = useState(searchTeam);
  const [filteredAssistanceData, setFilteredAssistanceData] = useState([]);

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Form Fields
  const [assistanceTeamId, setAssistanceTeamId] = useState(null);
  const [assistanceTeamName, setAssistanceTeamName] = useState('');
  const [selectedParentTeam, setSelectedParentTeam] = useState('');
  const [selectedParentTeamName, setSelectedParentTeamName] = useState('');
  const [selectedAssistanceLead, setSelectedAssistanceLead] = useState('');
  const [selectedNewAssistanceLead, setSelectedNewAssistanceLead] = useState('');
  const [selectedAssistanceLeadName, setSelectedAssistanceLeadName] = useState('');
  const [parentTeamsList, setParentTeamsList] = useState([]);
  const [eligibleLeads, setEligibleLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const [assistanceRowCount, setAssistanceRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState([{ field: 'id', sort: 'desc' }]);

  const pageSizeOptions = [5, 10, 15];
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const navigate = useNavigate();
  const isSearchFirstRender = useRef(true);

  // Fetch Assistance Teams
  const fetchAllAssistanceTeamsData = useCallback(async () => {
    setLoading(true);
    try {
      const clickedField = sortModel?.[0]?.field || 'id';
      const sortField = ASSISTANCE_TEAM_SORT_FIELD_MAP[clickedField] || clickedField;
      const sortDir = sortModel[0]?.sort || 'desc';
      const response = await assistanceTeamServices.getAllAssistanceTeams(
        debouncedSearchTeam,
        paginationModel.page,
        paginationModel.pageSize,
        sortField,
        sortDir
      );
      if (response && Array.isArray(response.content)) {
        setAssistanceTeamData(response.content);
      }
      setAssistanceRowCount(response?.totalElements || 0);
    } catch (error) {
      console.error("Error fetching Assistance Team data", error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTeam, paginationModel.page, paginationModel.pageSize, sortModel]);

  // Fetch Parent Teams List
  const fetchParentTeams = async () => {
    try {
      const response = await teamServices.getAllTeamsListForUserScreen();
      setParentTeamsList(response || []);
    } catch (error) {
      console.error('Error fetching parent teams:', error);
    }
  };

  // Fetch Eligible Leads when parent team changes
  const fetchEligibleLeadsForParentTeam = async (pTeamId) => {
    if (!pTeamId) {
      setEligibleLeads([]);
      return;
    }
    setLoadingLeads(true);
    try {
      const response = await assistanceTeamServices.getEligibleLeads(pTeamId);
      setEligibleLeads(response || []);
    } catch (error) {
      console.error('Error fetching eligible assistance team leads:', error);
      setEligibleLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchAllAssistanceTeamsData();
      fetchParentTeams();
    }
  }, [accessToken, fetchAllAssistanceTeamsData]);

  // Debounced search
  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchTeam.trim();
      if (value === "" || value.length > 2) {
        setDebouncedSearchTeam(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTeam]);

  // Filter assistance teams in memory
  useEffect(() => {
    const term = searchTeam.toLowerCase();
    const filtered = assistanceTeamData?.filter(at =>
      at.name?.toLowerCase().includes(term) ||
      (at.parentTeamName && at.parentTeamName.toLowerCase().includes(term)) ||
      (at.ownerFirstName && at.ownerFirstName.toLowerCase().includes(term)) ||
      (at.ownerLastName && at.ownerLastName.toLowerCase().includes(term)) ||
      (at.ownerEmail && at.ownerEmail.toLowerCase().includes(term))
    );
    setFilteredAssistanceData(filtered);
  }, [searchTeam, assistanceTeamData]);

  const handleOpenDialog = (event) => {
    if (event?.currentTarget) event.currentTarget.blur();
    setAssistanceTeamId(null);
    setIsEditing(false);
    setAssistanceTeamName('');
    setSelectedParentTeam('');
    setSelectedParentTeamName('');
    setSelectedAssistanceLead('');
    setSelectedNewAssistanceLead('');
    setSelectedAssistanceLeadName('');
    setEligibleLeads([]);
    fetchParentTeams();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAssistanceTeamId(null);
    setIsEditing(false);
    setAssistanceTeamName('');
    setSelectedParentTeam('');
    setSelectedParentTeamName('');
    setSelectedAssistanceLead('');
    setSelectedNewAssistanceLead('');
    setSelectedAssistanceLeadName('');
    setEligibleLeads([]);
  };

  const handleParentTeamChange = (pTeamId) => {
    setSelectedParentTeam(pTeamId);
    setSelectedAssistanceLead('');
    fetchEligibleLeadsForParentTeam(pTeamId);
  };

  const handleFormSubmit = () => {
    if (!assistanceTeamName || !selectedParentTeam || (!assistanceTeamId && !selectedAssistanceLead)) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    if (assistanceTeamId) {
      const updateData = {
        id: assistanceTeamId,
        name: assistanceTeamName,
        parentTeamId: selectedParentTeam,
        ownerId: selectedAssistanceLead,
        newOwner: selectedNewAssistanceLead || selectedAssistanceLead,
      };
      assistanceTeamServices.updateAssistanceTeamData(updateData)
        .then(() => {
          showSnackbar('Assistance Team updated successfully!', 'success');
          fetchAllAssistanceTeamsData();
          handleCloseDialog();
        })
        .catch((error) => {
          const msg = error.response?.data?.message || error.response?.data || 'Error updating assistance team';
          showSnackbar(typeof msg === 'string' ? msg : 'Error updating assistance team', 'error');
        });
    } else {
      const createData = {
        name: assistanceTeamName,
        parentTeamId: selectedParentTeam,
        ownerId: selectedAssistanceLead,
      };
      assistanceTeamServices.postAssistanceTeamData(createData)
        .then(() => {
          showSnackbar('Assistance Team added successfully!', 'success');
          fetchAllAssistanceTeamsData();
          handleCloseDialog();
        })
        .catch((error) => {
          const msg = error.response?.data?.message || error.response?.data || 'Error creating assistance team';
          showSnackbar(typeof msg === 'string' ? msg : 'Error creating assistance team', 'error');
        });
    }
  };

  const handleEditAssistanceTeam = useCallback((event, at) => {
    event.stopPropagation();
    if (event?.currentTarget) event.currentTarget.blur();
    setIsEditing(true);
    setAssistanceTeamId(at.id);
    setAssistanceTeamName(at.name || '');
    setSelectedParentTeam(at.parentTeamId || '');
    setSelectedParentTeamName(at.parentTeamName || '');
    setSelectedAssistanceLead(at.ownerId || '');
    setSelectedNewAssistanceLead('');
    setSelectedAssistanceLeadName(`${at.ownerFirstName || ''} ${at.ownerLastName || ''}`);
    fetchEligibleLeadsForParentTeam(at.parentTeamId);
    setOpenDialog(true);
  }, []);

  const handleRowClick = useCallback((rowData) => {
    navigate('/home/team-members', {
      state: {
        teamId: rowData.id,
        teamName: rowData.name,
        isAssistanceTeam: true,
        parentTeamId: rowData.parentTeamId,
        parentTeamName: rowData.parentTeamName
      }
    });
  }, [navigate]);

  // Columns for Assistance Teams
  const columns = useMemo(() => [
    {
      field: 'name', headerName: "Assistance Team Name", sortField: 'name', flex: 2, minWidth: 160, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'parentTeamName', headerName: "Parent Team", sortField: 'parentTeam.name', flex: 2, minWidth: 140, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'ownerFirstName', headerName: "Lead First Name", sortField: 'ownerId.firstName', flex: 1.5, minWidth: 130, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'ownerLastName', headerName: "Lead Last Name", sortField: 'ownerId.lastName', flex: 1.5, minWidth: 130, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'ownerEmail', headerName: "Lead Email Id", sortField: 'ownerId.emailId', flex: 2.5, minWidth: 180, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: "Actions",
      width: 120,
      minWidth: 110,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <IconButton
            color="primary"
            onClick={(event) => handleEditAssistanceTeam(event, params.row)}
            className="icon-button"
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="info"
            onClick={(event) => {
              event.stopPropagation();
              handleRowClick(params.row);
            }}
            className="icon-button"
            size="small"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ], [handleEditAssistanceTeam, handleRowClick]);

  return (
    <div className="data-table-container">
      <PageHeader title="Assistance Teams" />

      {/* Snackbar */}
      {SnackbarComponent}

      <div className="search-and-button">
        <Button
          variant="contained"
          onClick={handleOpenDialog}
          className="add-button"
          startIcon={<AddIcon />}
        >
          Add
        </Button>

        {/* Search Filter */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Search Assistance Teams..."
            value={searchTeam}
            onChange={(e) => setSearchTeam(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="data-table">
        <div className="data-grid-container">
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={6} />
            </Box>
          ) : (
            <DataGrid
              loading={false}
              rows={filteredAssistanceData}
              columns={columns}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              rowCount={assistanceRowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={setPaginationModel}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={(newModel) => {
                if (!newModel?.length && sortModel.length > 0) {
                  setSortModel(sortModel);
                } else {
                  setSortModel(newModel);
                }
              }}
              onRowClick={(params) => handleRowClick(params.row)}
              getRowClassName={() => 'clickable-row'}
            />
          )}
        </div>

        {/* Dialog for Add / Edit Assistance Team */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          disableRestoreFocus
          slotProps={{ paper: { className: 'modern-dialog' } }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className="modern-dialog-title">
            <span className="modern-dialog-title-text">
              {isEditing ? "Update Assistance Team" : "Add Assistance Team"}
            </span>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              className="modern-dialog-close-btn"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className="modern-dialog-content">
            <Grid2 container spacing={2.5}>
              {/* Parent Team Selection */}
              <Grid2 size={{ xs: 12 }}>
                {isEditing ? (
                  <TextField
                    label="Parent Team"
                    value={selectedParentTeamName ?? ''}
                    fullWidth
                    disabled
                  />
                ) : (
                  <FormControl fullWidth required>
                    <InputLabel>Parent Team</InputLabel>
                    <Select
                      value={(Array.isArray(parentTeamsList) && parentTeamsList.some((pt) => String(pt.id) === String(selectedParentTeam))) ? selectedParentTeam : ''}
                      onChange={(e) => handleParentTeamChange(e.target.value)}
                      label="Parent Team"
                    >
                      {parentTeamsList.map((pt) => (
                        <MenuItem key={pt.id} value={pt.id}>
                          {pt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid2>

              {/* Assistance Team Name */}
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  label="Assistance Team Name"
                  value={assistanceTeamName ?? ''}
                  onChange={(e) => setAssistanceTeamName(e.target.value)}
                  fullWidth
                  required
                />
              </Grid2>

              {/* Assistance Team Lead */}
              {isEditing ? (
                <>
                  <Grid2 size={{ xs: 12 }}>
                    <TextField
                      label="Current Lead"
                      value={selectedAssistanceLeadName ?? ''}
                      fullWidth
                      disabled
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <InputLabel>New Lead</InputLabel>
                      <Select
                        value={(Array.isArray(eligibleLeads) && eligibleLeads.some((u) => String(u.id) === String(selectedNewAssistanceLead))) ? selectedNewAssistanceLead : ''}
                        onChange={(e) => setSelectedNewAssistanceLead(e.target.value)}
                        label="New Lead"
                      >
                        {eligibleLeads.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {[user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                </>
              ) : (
                <Grid2 size={{ xs: 12 }}>
                  <FormControl fullWidth required disabled={!selectedParentTeam || loadingLeads}>
                    <InputLabel>Assistance Team Lead</InputLabel>
                    <Select
                      value={(Array.isArray(eligibleLeads) && eligibleLeads.some((u) => String(u.id) === String(selectedAssistanceLead))) ? selectedAssistanceLead : ''}
                      onChange={(e) => setSelectedAssistanceLead(e.target.value)}
                      label="Assistance Team Lead"
                    >
                      {eligibleLeads.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {[user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid2>
              )}
            </Grid2>
          </DialogContent>
          <DialogActions className="modern-dialog-actions">
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              color="inherit"
              sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFormSubmit}
              variant="contained"
              color="primary"
              disableElevation
            >
              {isEditing ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default AssistanceTeams;
